<script setup lang="ts">
import { computed, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import DragArea from "~/components/dumb/DragArea.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import GenericSubdropdown from "~/components/dumb/GenericSubdropdown.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import PageEmptyState from "~/components/dumb/PageEmptyState.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import PageCard from "~/components/PageCard.vue";
import {
  CreatedFieldIcon,
  LastUpdatedFieldIcon,
  PlusIcon,
  SortAscendingIcon,
  SortDescendingIcon,
  SortIcon,
  StandardTextIcon,
  UnsortedIcon,
} from "~/icons";
import { ButtonStyle, CommandId, DropdownMenuItemKind, FolderSort, IconSize } from "~/shared/enums";
import type { Doc } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { makeStringComparator } from "~/utils/comparator";
import useLastScrollPosition from "~/utils/useLastScrollPosition";
import usePaginatedList from "~/utils/usePaginatedList";

const props = defineProps<{
  folderDuid: string;
  slugSep?: string;
  slug?: string;
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const sort = computed(() => userStore.layout?.docSort || FolderSort.ORDER);

const folder = computed(() => dataStore.getFolderByDuid(props.folderDuid));
const allDocsInFolder = computed(() => dataStore.getLoadedDocsByFolderDuidOrdered(props.folderDuid, { order: null }));
const docs = computed(() => dataStore.getLoadedDocsByFolderDuidOrdered(props.folderDuid, { order: sort.value }));

const scrollContainer = ref<HTMLDivElement | null>(null);
const { isLoading, canLoadMore, reset } = usePaginatedList(
  computed(() => `docs-${props.folderDuid}-${sort.value}`),
  scrollContainer,
  computed(() => docs.value.length),
  async () => (!folder.value ? 0 : dataStore.loadPaginatedDocsForFolderDuid(folder.value.duid, sort.value))
);
useLastScrollPosition(
  computed(() => props.folderDuid + sort.value),
  scrollContainer
);

const docsSorted = computed(() => {
  const res = [...(isLoading ? allDocsInFolder.value : docs.value)];
  switch (sort.value) {
    case FolderSort.ORDER: {
      res.sort(makeStringComparator((doc) => doc.order));
      break;
    }
    case FolderSort.RECENT_ASC: {
      res.sort(makeStringComparator((doc) => doc.updatedAt, false));
      break;
    }
    case FolderSort.RECENT_DESC: {
      res.sort(makeStringComparator((doc) => doc.updatedAt, true));
      break;
    }
    case FolderSort.CREATED_ASC: {
      res.sort(makeStringComparator((doc) => doc.createdAt, false));
      break;
    }
    case FolderSort.CREATED_DESC: {
      res.sort(makeStringComparator((doc) => doc.createdAt, true));
      break;
    }
    case FolderSort.TITLE_ASC: {
      res.sort(makeStringComparator((doc) => doc.title.toLowerCase(), false));
      break;
    }
    case FolderSort.TITLE_DESC: {
      res.sort(makeStringComparator((doc) => doc.title.toLowerCase(), true));
      break;
    }

    default: {
      throw new Error(`Unknown sort: ${sort.value}`);
    }
  }
  return res;
});

watch(
  () => props.folderDuid,
  () => {
    reset();
  }
);

const changeSort = (newSort: FolderSort) => {
  if (newSort === sort.value) {
    return;
  }
  userStore.setDocSort(newSort);
  reset();
};

const sortTitle = computed(() => {
  if (sort.value === FolderSort.ORDER) {
    return "Sort";
  }
  if (sort.value === FolderSort.TITLE_ASC || sort.value === FolderSort.TITLE_DESC) {
    return "Title";
  }
  if (sort.value === FolderSort.CREATED_ASC || sort.value === FolderSort.CREATED_DESC) {
    return "Created";
  }
  return "Last updated";
});

const sortIcon = computed(() => {
  if (sort.value === FolderSort.ORDER) {
    return undefined;
  }
  return sort.value === FolderSort.TITLE_ASC ||
    sort.value === FolderSort.CREATED_ASC ||
    sort.value === FolderSort.RECENT_ASC
    ? SortAscendingIcon
    : SortDescendingIcon;
});

const createDocAndStartEditing = () => {
  if (!appStore.spaceOrFolder) {
    return;
  }
  appStore.spaceOrFolder.createDoc();
};

const getComponentProps = (doc: Doc) => ({
  page: doc,
});

const moveDoc = (_: string, doc: Doc) => {
  dataStore.updateDocs([
    {
      duid: doc.duid,
      order: doc.order,
    },
  ]);
};

const createSortItems = (title: string, ascending: FolderSort, descending: FolderSort) => ({
  kind: DropdownMenuItemKind.COMPONENT,
  title,
  component: GenericSubdropdown,
  componentArgs: {
    title,
    icon: title === "Title" ? StandardTextIcon : title === "Created" ? CreatedFieldIcon : LastUpdatedFieldIcon,
    iconAfter:
      sort.value === ascending ? SortAscendingIcon : sort.value === descending ? SortDescendingIcon : undefined,
    widthPixels: 160,
    sections: [
      {
        title: "Direction",
        items: [
          {
            kind: DropdownMenuItemKind.BUTTON,
            title: "Ascending",
            icon: SortAscendingIcon,
            selected: sort.value === ascending,
            onClick: () => changeSort(ascending),
          },
          {
            kind: DropdownMenuItemKind.BUTTON,
            title: "Descending",
            icon: SortDescendingIcon,
            selected: sort.value === descending,
            onClick: () => changeSort(descending),
          },
        ],
      },
    ],
  },
});

const dropdownSections = computed(() => [
  {
    title: "Sort by",
    items: [
      createSortItems("Title", FolderSort.TITLE_ASC, FolderSort.TITLE_DESC),
      createSortItems("Created", FolderSort.CREATED_ASC, FolderSort.CREATED_DESC),
      createSortItems("Last updated", FolderSort.RECENT_ASC, FolderSort.RECENT_DESC),
      {
        title: "Remove sort",
        hidden: sort.value === FolderSort.ORDER,
        kind: DropdownMenuItemKind.BUTTON,
        icon: UnsortedIcon,
        onClick: () => changeSort(FolderSort.ORDER),
      },
    ],
  },
]);
</script>

<template>
  <div v-if="folder" ref="scrollContainer" class="flex size-full justify-center overflow-y-scroll pl-[15px]">
    <div
      class="flex h-fit w-full max-w-xl grow flex-col px-6 extra-overscroll"
      :class="pageStore.isMobile ? 'mt-6' : 'mt-28'">
      <template v-if="(isLoading && allDocsInFolder.length !== 0) || docs.length > 0">
        <div class="mx-[15px] max-w-full select-none truncate text-2xl font-semibold text-hvy">
          {{ folder.title }}
        </div>
        <div class="flex items-end justify-between border-b px-2 pt-2 border-md">
          <DropdownMenu :sections="dropdownSections" cover>
            <Tooltip text="Sort docs" class="mb-2">
              <Button
                :btn-style="ButtonStyle.SECONDARY"
                :text="sortTitle"
                text-style="text-lt"
                :icon="
                  sortIcon
                    ? sortTitle === 'Title'
                      ? StandardTextIcon
                      : sortTitle === 'Created'
                        ? CreatedFieldIcon
                        : LastUpdatedFieldIcon
                    : SortIcon
                "
                :icon-after="sortIcon"
                :icon-size="IconSize.S"
                borderless
                a11y-label="Sort docs"
                class="!gap-1 !px-1 !py-0.5" />
            </Tooltip>
          </DropdownMenu>
          <Tooltip :command-id="CommandId.CREATE_DOC" class="mb-2">
            <Button
              :btn-style="ButtonStyle.SECONDARY"
              :icon="PlusIcon"
              :icon-size="IconSize.M"
              borderless
              class="!p-0.5"
              a11y-label="Create a doc"
              @click="createDocAndStartEditing" />
          </Tooltip>
        </div>
      </template>
      <DragArea
        :no-reorder="sort !== FolderSort.ORDER"
        :no-reorder-to-last="canLoadMore"
        group="doc-editor"
        category="docs"
        class="!h-auto w-full divide-y divide-lt"
        :items="docsSorted"
        :component="PageCard"
        :get-component-props="getComponentProps"
        @change="moveDoc" />
      <LoadingSpinner v-if="isLoading" class="mt-4" />
      <PageEmptyState v-else-if="docs.length === 0" />
    </div>
  </div>
</template>
